import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { formatConstant } from '@/modules/common/lib/string';
import {
  ILearningBudgetTransactionInTheList,
  LearningBudgetTransactionStatus,
} from '@/types/learning-budget';
import { Chip, Stack, Typography } from '@mui/material';
import { format, isValid } from 'date-fns';
import { MRT_ColumnDef } from 'material-react-table';

export const LEARNING_BUDGET_TRANSACTIONS_TABLE_COLUMNS: MRT_ColumnDef<ILearningBudgetTransactionInTheList>[] =
  [
    {
      id: 'createdAt',
      header: 'Date',
      maxSize: 120,
      Cell: ({ row: { original: transaction } }) => {
        return (
          <div className="flex flex-col gap-2 max-w-full text-wrap break-words">
            <Typography className="font-medium text-xs">
              {isValid(new Date(transaction.createdAt))
                ? format(transaction.createdAt, 'dd MMMM yyyy HH:mm')
                : 'N/A'}
            </Typography>
            {transaction.labels?.map(label => (
              <Chip
                key={label}
                size="small"
                className="uppercase text-xxs"
                label={formatConstant(label)}
              />
            ))}
          </div>
        );
      },
    },
    {
      id: 'employeeInfo',
      header: 'Employee',
      Cell: ({ row: { original: transaction } }) => {
        return (
          <div className="flex flex-col gap-1 max-w-full text-wrap break-words">
            <Typography variant="caption">
              {transaction?.user?._id ?? '-'}
            </Typography>
            <Typography className="font-semibold" variant="body2">
              {[transaction?.user?.firstName, transaction?.user?.lastName]
                .filter(Boolean)
                .join(' ') || '-'}
            </Typography>
          </div>
        );
      },
    },
    {
      id: 'teamName',
      header: 'Team',
      Cell: ({ row: { original: transaction } }) => {
        return (
          <Typography className="font-medium max-w-full text-wrap break-words">
            {transaction?.team?.name ?? '-'}
          </Typography>
        );
      },
    },
    {
      id: 'courseDetails',
      header: 'Course Details',
      Cell: ({ row: { original: transaction } }) =>
        transaction?.order?.item ? (
          <Stack className="max-w-full text-wrap break-words">
            <Typography variant="body2" className="font-semibold">
              {transaction?.order?.item?.course?.name ?? '-'}
            </Typography>
            <Typography variant="body2">
              {transaction?.order?.item?.course?.educationProvider?.name ?? '-'}
            </Typography>
            <Typography variant="body2">
              {transaction?.order?.totalAmount
                ? formatCurrencyValue(
                    transaction?.order?.totalAmount,
                    transaction?.order?.currency ?? 'EUR',
                  )
                : '-'}
            </Typography>
          </Stack>
        ) : (
          '-'
        ),
    },
    {
      id: 'amountAvailableAtTransaction',
      header: 'Amount Available at Transaction',
      accessorFn: ({ amountAvailableAtTransaction }) => {
        const isValid = Number.isFinite(amountAvailableAtTransaction);
        const formattedAmount = isValid
          ? formatCurrencyValue(amountAvailableAtTransaction, 'EUR')
          : '-';

        return (
          <Typography className="max-w-full text-wrap break-words">
            {formattedAmount}
          </Typography>
        );
      },
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorFn: ({ amount }) => {
        const isValid = Number.isFinite(amount);
        const formattedAmount = isValid
          ? formatCurrencyValue(amount, 'EUR')
          : '-';

        return (
          <Typography
            className={`${isValid && amount < 0 ? 'font-medium text-error' : ''} max-w-full text-wrap break-words`}
          >
            {formattedAmount}
          </Typography>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      muiTableHeadCellProps: {
        align: 'center',
      },
      muiTableBodyCellProps: {
        align: 'center',
      },
      maxSize: 100,
      Cell: ({ row: { original: transaction } }) => (
        <Chip
          classes={{
            root: 'max-w-full text-wrap break-words',
          }}
          label={transaction.status}
          className={`capitalize rounded-sm text-primary ${
            transaction.status === LearningBudgetTransactionStatus.PENDING
              ? 'bg-warning-light'
              : transaction.status === LearningBudgetTransactionStatus.COMPLETE
                ? 'bg-success-light'
                : 'bg-error-light'
          }`}
          size="small"
        />
      ),
    },
  ];
